import React, { useEffect, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "../axios";
import { canUseAnalysis } from "../utils/analysisAccess";

const S = {
  page: {
    minHeight: "100vh",
    padding: "16px",
    background: "linear-gradient(180deg, #ecfdf5 0%, #f0fdf4 100%)",
    fontFamily: "sans-serif",
  },
  title: { textAlign: "center", color: "#047857", marginBottom: "24px" },
  card: {
    background: "#fff",
    borderRadius: "12px",
    padding: "20px",
    marginBottom: "20px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.07)",
  },
  cardTitle: {
    fontSize: "0.85rem",
    fontWeight: "700",
    color: "#047857",
    marginBottom: "14px",
    paddingBottom: "8px",
    borderBottom: "2px solid #d1fae5",
  },
  row: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "8px 0",
    borderBottom: "1px solid #f0fdf4",
    fontSize: "0.9rem",
  },
  label: { color: "#6b7280", minWidth: "80px" },
  value: { color: "#1f2937", fontWeight: "500" },
  input: {
    border: "1px solid #d1fae5",
    borderRadius: "8px",
    padding: "6px 10px",
    // iOS は 16px 未満だと自動でズームするので下回らせない
    fontSize: "16px",
    width: "100%",
    boxSizing: "border-box",
    outline: "none",
    color: "#1f2937",
  },
  btnPrimary: {
    background: "#047857",
    color: "#fff",
    border: "none",
    borderRadius: "8px",
    padding: "8px 18px",
    fontWeight: "600",
    cursor: "pointer",
    fontSize: "0.85rem",
  },
  btnSecondary: {
    background: "#e5e7eb",
    color: "#374151",
    border: "none",
    borderRadius: "8px",
    padding: "8px 18px",
    fontWeight: "600",
    cursor: "pointer",
    fontSize: "0.85rem",
  },
  btnDanger: {
    background: "none",
    color: "#dc2626",
    border: "1px solid #dc2626",
    borderRadius: "8px",
    padding: "4px 10px",
    fontWeight: "600",
    cursor: "pointer",
    fontSize: "0.8rem",
  },
  table: { width: "100%", borderCollapse: "collapse", fontSize: "0.82rem" },
  th: {
    background: "#34d399",
    color: "#fff",
    padding: "8px 6px",
    textAlign: "left",
    fontWeight: "600",
  },
  td: {
    padding: "8px 6px",
    borderBottom: "1px solid #d1fae5",
    color: "#374151",
    verticalAlign: "middle",
  },
  pageBtn: (active) => ({
    margin: "0 3px",
    padding: "5px 10px",
    borderRadius: "6px",
    border: "none",
    cursor: "pointer",
    background: active ? "#047857" : "#a7f3d0",
    color: active ? "#fff" : "#047857",
    fontWeight: "600",
    fontSize: "0.8rem",
  }),
  errorMsg: {
    color: "#dc2626",
    fontSize: "0.82rem",
    marginTop: "6px",
  },
  successMsg: {
    color: "#047857",
    fontSize: "0.82rem",
    marginTop: "6px",
  },
};

export default function MyPage({ currentUser, setCurrentUser }) {
  const navigate = useNavigate();

  const [profile, setProfile]     = useState(null);
  const [editing, setEditing]     = useState(false);
  const [form, setForm]           = useState({ name: "", email: "" });
  const [saving, setSaving]       = useState(false);
  const [formError, setFormError] = useState("");
  const [formSuccess, setFormSuccess] = useState("");
  const [emailChangedMsg, setEmailChangedMsg] = useState(false);

  const [favorites, setFavorites]         = useState([]);
  const [favInput, setFavInput]           = useState("");
  const [favAdding, setFavAdding]         = useState(false);
  const [favError, setFavError]           = useState("");
  const [favDeletingId, setFavDeletingId] = useState(null);
  const [favConfirm, setFavConfirm]       = useState(null); // 確認中のバス情報 { bus_number, depot, retired_at }
  const [favLooking, setFavLooking]       = useState(false);

  const [sightings, setSightings]   = useState([]);
  const [page, setPage]             = useState(1);
  const [lastPage, setLastPage]     = useState(1);
  const [total, setTotal]           = useState(0);
  const [loadingSightings, setLoadingSightings] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [showScrollHint, setShowScrollHint] = useState(false);
  const tableWrapRef = useRef(null);

  const EMPTY_PUBLIC = { bio: "", activity_area: "", x_id: "", instagram_id: "", website_url: "" };
  const [publicEditing, setPublicEditing]     = useState(false);
  const [publicForm, setPublicForm]           = useState(EMPTY_PUBLIC);
  const [publicSaving, setPublicSaving]       = useState(false);
  const [publicError, setPublicError]         = useState("");
  const [publicSuccess, setPublicSuccess]     = useState("");

  const [settingsSaving, setSettingsSaving] = useState(false);
  const [settingsError, setSettingsError]   = useState("");

  useEffect(() => {
    axios.get("/user/favorites").then((res) => setFavorites(res.data)).catch(() => {});
  }, []);

  useEffect(() => {
    axios.get("/user/profile").then((res) => {
      setProfile(res.data);
      setForm({ name: res.data.name, email: res.data.email });
      setPublicForm({
        bio:           res.data.bio           ?? "",
        activity_area: res.data.activity_area ?? "",
        x_id:          res.data.x_id          ?? "",
        instagram_id:  res.data.instagram_id  ?? "",
        website_url:   res.data.website_url   ?? "",
      });
    });
  }, []);

  useEffect(() => {
    if (!tableWrapRef.current) return;
    const el = tableWrapRef.current;
    const check = () => setShowScrollHint(el.scrollLeft === 0 && el.scrollWidth > el.clientWidth);
    check();
    el.addEventListener("scroll", check);
    window.addEventListener("resize", check);
    return () => {
      el.removeEventListener("scroll", check);
      window.removeEventListener("resize", check);
    };
  }, [sightings]);

  useEffect(() => {
    setLoadingSightings(true);
    axios
      .get(`/user/sightings?page=${page}`)
      .then((res) => {
        setSightings(res.data.data);
        setLastPage(res.data.last_page);
        setTotal(res.data.total);
      })
      .finally(() => setLoadingSightings(false));
  }, [page]);

  const handleSave = async () => {
    setFormError("");
    setFormSuccess("");
    if (!form.name.trim()) {
      setFormError("名前を入力してください");
      return;
    }
    setSaving(true);
    try {
      const res = await axios.put("/user/profile", form);

      if (res.data.email_changed) {
        // トークン削除済み・再認証が必要
        localStorage.removeItem("token");
        setCurrentUser(null);
        setEditing(false);
        setEmailChangedMsg(true);
        setTimeout(() => navigate("/login"), 4000);
        return;
      }

      setProfile((prev) => ({ ...prev, name: res.data.name, email: res.data.email }));
      setCurrentUser((prev) => ({ ...prev, name: res.data.name }));
      setEditing(false);
      setFormSuccess("保存しました");
      setTimeout(() => setFormSuccess(""), 3000);
    } catch (e) {
      const msg = e.response?.data?.errors
        ? Object.values(e.response.data.errors).flat().join(" / ")
        : "保存に失敗しました";
      setFormError(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (s) => {
    if (!window.confirm(`この投稿を削除しますか？\n\n${s.started_at}　${s.bus_number}号車　${s.route}`)) return;
    setDeletingId(s.id);
    try {
      await axios.delete(`/sightings/${s.id}`);
      setSightings((prev) => prev.filter((x) => x.id !== s.id));
      setTotal((prev) => prev - 1);
    } catch {
      alert("削除に失敗しました");
    } finally {
      setDeletingId(null);
    }
  };

  const handleCancel = () => {
    setForm({ name: profile.name, email: profile.email });
    setFormError("");
    setEditing(false);
  };

  const handlePublicSave = async () => {
    setPublicError("");
    setPublicSuccess("");
    if (publicForm.website_url && !/^https?:\/\/.+/.test(publicForm.website_url)) {
      setPublicError("URLはhttp://またはhttps://から始めてください");
      return;
    }
    setPublicSaving(true);
    try {
      const res = await axios.put("/user/public-profile", publicForm);
      setProfile((prev) => ({ ...prev, ...res.data }));
      setPublicEditing(false);
      setPublicSuccess("保存しました");
      setTimeout(() => setPublicSuccess(""), 3000);
    } catch (e) {
      const msg = e.response?.data?.errors
        ? Object.values(e.response.data.errors).flat().join(" / ")
        : "保存に失敗しました";
      setPublicError(msg);
    } finally {
      setPublicSaving(false);
    }
  };

  const handlePublicCancel = () => {
    setPublicForm({
      bio:           profile.bio           ?? "",
      activity_area: profile.activity_area ?? "",
      x_id:          profile.x_id          ?? "",
      instagram_id:  profile.instagram_id  ?? "",
      website_url:   profile.website_url   ?? "",
    });
    setPublicError("");
    setPublicEditing(false);
  };

  // 切り替えたらその場で保存する（保存ボタンを置くほどの設定ではない）
  const handleRegularMarksChange = async (checked) => {
    setSettingsError("");
    setSettingsSaving(true);
    try {
      const res = await axios.put("/user/settings", { show_regular_marks: checked });
      const value = res.data.show_regular_marks;
      setProfile((prev) => ({ ...prev, show_regular_marks: value }));
      setCurrentUser((prev) => (prev ? { ...prev, show_regular_marks: value } : prev));
    } catch {
      setSettingsError("保存に失敗しました");
    } finally {
      setSettingsSaving(false);
    }
  };

  const handleFavConfirm = async () => {
    setFavAdding(true);
    try {
      const res = await axios.post("/user/favorites", { bus_number: favConfirm.bus_number });
      setFavorites((prev) => [...prev, res.data].sort((a, b) => a.bus_number.localeCompare(b.bus_number, undefined, { numeric: true })));
      setFavInput("");
      setFavConfirm(null);
    } catch (e) {
      setFavError(e.response?.data?.message || "追加に失敗しました");
      setFavConfirm(null);
    } finally {
      setFavAdding(false);
    }
  };

  if (emailChangedMsg) {
    return (
      <div style={S.page}>
        <div style={{
          maxWidth: "400px",
          margin: "60px auto",
          background: "#fff",
          borderRadius: "12px",
          padding: "32px 24px",
          textAlign: "center",
          boxShadow: "0 2px 8px rgba(0,0,0,0.07)",
        }}>
          <div style={{ fontSize: "2rem", marginBottom: "12px" }}>📧</div>
          <p style={{ color: "#047857", fontWeight: "700", marginBottom: "8px" }}>
            確認メールを送信しました
          </p>
          <p style={{ color: "#6b7280", fontSize: "0.85rem", lineHeight: "1.6" }}>
            新しいメールアドレスに届いた確認メールのリンクをクリックして再認証してください。
            <br />まもなくログイン画面へ移動します。
          </p>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div style={S.page}>
        <div style={{ textAlign: "center", color: "#065f46" }}>読み込み中...</div>
      </div>
    );
  }

  return (
    <div style={S.page}>
      <h2 style={S.title}>マイページ</h2>

      {/* プロフィールカード */}
      <div style={S.card}>
        <div style={S.cardTitle}>プロフィール</div>

        {!editing ? (
          <>
            <div style={S.row}>
              <span style={S.label}>名前</span>
              <span style={S.value}>{profile.name}</span>
            </div>
            <div style={S.row}>
              <span style={S.label}>メール</span>
              <span style={S.value}>{profile.email}</span>
            </div>
            <div style={S.row}>
              <span style={S.label}>登録日</span>
              <span style={S.value}>{profile.created_at}</span>
            </div>
            <div style={{ ...S.row, borderBottom: "none" }}>
              <span style={S.label}>総投稿数</span>
              <span style={{ ...S.value, color: "#047857" }}>
                {profile.sightings_count} 件
              </span>
            </div>
            {formSuccess && <p style={S.successMsg}>{formSuccess}</p>}
            <div style={{ marginTop: "16px", textAlign: "right" }}>
              <button style={S.btnPrimary} onClick={() => setEditing(true)}>
                編集
              </button>
            </div>
          </>
        ) : (
          <>
            <div style={{ marginBottom: "12px" }}>
              <label style={{ ...S.label, display: "block", marginBottom: "4px" }}>
                名前
              </label>
              <input
                style={S.input}
                value={form.name}
                onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                maxLength={50}
              />
            </div>
            <div style={{ marginBottom: "12px" }}>
              <label style={{ ...S.label, display: "block", marginBottom: "4px" }}>
                メールアドレス
              </label>
              <input
                style={S.input}
                type="email"
                value={form.email}
                onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
              />
              {form.email !== profile.email && (
                <p style={{ color: "#d97706", fontSize: "0.78rem", marginTop: "4px" }}>
                  ※ メールアドレスを変更すると再認証が必要になります
                </p>
              )}
            </div>
            {formError && <p style={S.errorMsg}>{formError}</p>}
            <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end", marginTop: "12px" }}>
              <button style={S.btnSecondary} onClick={handleCancel} disabled={saving}>
                キャンセル
              </button>
              <button style={S.btnPrimary} onClick={handleSave} disabled={saving}>
                {saving ? "保存中..." : "保存"}
              </button>
            </div>
          </>
        )}
      </div>

      {/* 公開プロフィールカード */}
      <div style={S.card}>
        <div style={S.cardTitle}>公開プロフィール</div>

        {!publicEditing ? (
          <>
            <div style={S.row}>
              <span style={S.label}>自己紹介</span>
              <span style={{ ...S.value, color: profile.bio ? "#1f2937" : "#9ca3af", wordBreak: "break-word" }}>
                {profile.bio
                  ? profile.bio.split('\n').map((line, i, arr) => (
                      <React.Fragment key={i}>
                        {line}
                        {i < arr.length - 1 && <br />}
                      </React.Fragment>
                    ))
                  : "未設定"}
              </span>
            </div>
            {[
              { label: "主な活動拠点", value: profile.activity_area },
              { label: "X (Twitter)", value: profile.x_id ? `@${profile.x_id}` : null },
              { label: "Instagram",   value: profile.instagram_id ? `@${profile.instagram_id}` : null },
              { label: "URL",         value: profile.website_url },
            ].map(({ label, value }) => (
              <div key={label} style={S.row}>
                <span style={S.label}>{label}</span>
                <span style={{ ...S.value, color: value ? "#1f2937" : "#9ca3af", wordBreak: "break-all" }}>
                  {value || "未設定"}
                </span>
              </div>
            ))}
            {publicSuccess && <p style={S.successMsg}>{publicSuccess}</p>}
            <div style={{ marginTop: "16px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <a
                href={`/users/${profile.id}`}
                target="_blank"
                rel="noreferrer"
                style={{ fontSize: "0.78rem", color: "#047857" }}
              >
                公開ページを確認 →
              </a>
              <button style={S.btnPrimary} onClick={() => setPublicEditing(true)}>
                編集
              </button>
            </div>
          </>
        ) : (
          <>
            {[
              { key: "bio",           label: "自己紹介",     max: 100, placeholder: "100文字以内で自己紹介を書いてください", textarea: true },
              { key: "activity_area", label: "主な活動拠点", max: 50,  placeholder: "例: 福岡市内、北九州エリアなど" },
              { key: "x_id",         label: "X アカウントID", max: 50, placeholder: "@ を除いたID（例: example）" },
              { key: "instagram_id", label: "Instagram ID",  max: 50, placeholder: "@ を除いたID（例: example）" },
              { key: "website_url",  label: "URL（HPなど）", max: 255, placeholder: "https://example.com" },
            ].map(({ key, label, max, placeholder, textarea }) => (
              <div key={key} style={{ marginBottom: "12px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "4px" }}>
                  <label style={{ ...S.label }}>{label}</label>
                  <span style={{ fontSize: "0.72rem", color: "#9ca3af" }}>
                    {(publicForm[key] || "").length} / {max}
                  </span>
                </div>
                {textarea ? (
                  <textarea
                    style={{ ...S.input, resize: "vertical", lineHeight: "1.6" }}
                    value={publicForm[key] || ""}
                    onChange={(e) => setPublicForm((p) => ({ ...p, [key]: e.target.value }))}
                    maxLength={max}
                    placeholder={placeholder}
                    rows={3}
                  />
                ) : (
                  <input
                    style={S.input}
                    value={publicForm[key] || ""}
                    onChange={(e) => setPublicForm((p) => ({ ...p, [key]: e.target.value }))}
                    maxLength={max}
                    placeholder={placeholder}
                  />
                )}
              </div>
            ))}
            {publicError && <p style={S.errorMsg}>{publicError}</p>}
            <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end", marginTop: "12px" }}>
              <button style={S.btnSecondary} onClick={handlePublicCancel} disabled={publicSaving}>
                キャンセル
              </button>
              <button style={S.btnPrimary} onClick={handlePublicSave} disabled={publicSaving}>
                {publicSaving ? "保存中..." : "保存"}
              </button>
            </div>
          </>
        )}
      </div>

      {/* お気に入り車両カード */}
      <div style={S.card}>
        <div style={{ ...S.cardTitle, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <span>
            お気に入り車両
            <span style={{ marginLeft: "8px", color: "#6b7280", fontWeight: "400" }}>
              {favorites.length} / 100台
            </span>
          </span>
          <Link to="/favorites" style={{ fontSize: "0.78rem", color: "#047857", textDecoration: "none" }}>
            一覧ページへ →
          </Link>
        </div>

        {/* 追加フォーム */}
        <div style={{ display: "flex", gap: "8px", marginBottom: "12px" }}>
          <input
            style={{ ...S.input, flex: 1 }}
            value={favInput}
            onChange={(e) => { setFavInput(e.target.value); setFavError(""); }}
            placeholder="車号を入力（例: 1001）"
            inputMode="numeric"
            maxLength={6}
          />
          <button
            style={{ ...S.btnPrimary, whiteSpace: "nowrap" }}
            disabled={favLooking || !favInput.trim()}
            onClick={async () => {
              setFavError("");
              setFavLooking(true);
              try {
                const res = await axios.get(`/buses/lookup?bus_number=${encodeURIComponent(favInput.trim())}`);
                setFavConfirm(res.data);
              } catch (e) {
                setFavError(e.response?.data?.error || "入力した車号の車両は見つかりません");
              } finally {
                setFavLooking(false);
              }
            }}
          >
            {favLooking ? "…" : "追加"}
          </button>
        </div>
        {favError && <p style={S.errorMsg}>{favError}</p>}

        {/* 一覧 */}
        {favorites.length === 0 ? (
          <p style={{ color: "#9ca3af", fontSize: "0.85rem", textAlign: "center", padding: "12px 0" }}>
            登録されていません
          </p>
        ) : (
          <div style={{ maxHeight: "220px", overflowY: "auto" }}>
            {favorites.map((bus) => (
              <div key={bus.bus_id} style={{ ...S.row, gap: "8px" }}>
                <span style={{ ...S.value, minWidth: "48px" }}>{bus.bus_number}</span>
                <span style={{ color: "#9ca3af", fontSize: "0.82rem", flex: 1 }}>{bus.depot}</span>
                {bus.retired_at && (
                  <span style={{ fontSize: "0.72rem", color: "#dc2626", background: "#fee2e2", borderRadius: "4px", padding: "1px 5px", fontWeight: "700" }}>廃車</span>
                )}
                <button
                  style={{ ...S.btnDanger, flexShrink: 0, opacity: favDeletingId === bus.bus_id ? 0.5 : 1 }}
                  disabled={favDeletingId === bus.bus_id}
                  onClick={async () => {
                    setFavDeletingId(bus.bus_id);
                    try {
                      await axios.delete(`/user/favorites/${bus.bus_id}`);
                      setFavorites((prev) => prev.filter((b) => b.bus_id !== bus.bus_id));
                    } catch {
                      alert("削除に失敗しました");
                    } finally {
                      setFavDeletingId(null);
                    }
                  }}
                >
                  {favDeletingId === bus.bus_id ? "…" : "削除"}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* 表示設定カード（所定の印は運用解析と同じ範囲にだけ出すので、設定もそこだけ） */}
      {canUseAnalysis(currentUser) && (
        <div style={S.card}>
          <div style={S.cardTitle}>表示設定</div>
          <label style={{ display: "flex", alignItems: "flex-start", gap: "10px", cursor: "pointer", fontSize: "0.9rem" }}>
            <input
              type="checkbox"
              checked={profile.show_regular_marks !== false}
              disabled={settingsSaving}
              onChange={(e) => handleRegularMarksChange(e.target.checked)}
              style={{ marginTop: "4px", width: "16px", height: "16px", accentColor: "#047857" }}
            />
            <span>
              <span style={{ color: "#1f2937", fontWeight: "500" }}>
                目撃情報の表に所定運用の印（<span style={{ color: "#0ea5e9" }}>◆</span>）を出す
              </span>
              <span style={{ display: "block", color: "#6b7280", fontSize: "0.78rem", marginTop: "2px", lineHeight: "1.5" }}>
                その日の朝に所定だった車が、その乗番の便を走ったときに付きます
              </span>
            </span>
          </label>
          {settingsError && <p style={S.errorMsg}>{settingsError}</p>}
        </div>
      )}

      {/* 投稿履歴カード */}
      <div style={S.card}>
        <div style={S.cardTitle}>
          投稿履歴
          <span style={{ marginLeft: "8px", color: "#6b7280", fontWeight: "400" }}>
            全 {total} 件
          </span>
        </div>

        {loadingSightings ? (
          <div style={{ textAlign: "center", color: "#065f46", padding: "20px" }}>
            読み込み中...
          </div>
        ) : sightings.length === 0 ? (
          <div style={{ textAlign: "center", color: "#9ca3af", padding: "20px" }}>
            投稿がありません
          </div>
        ) : (
          <>
            {showScrollHint && (
              <div style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
                gap: "4px",
                fontSize: "0.72rem",
                color: "#9ca3af",
                marginBottom: "4px",
                paddingRight: "2px",
              }}>
                <span>横にスクロールできます</span>
                <span style={{ fontSize: "0.85rem" }}>→</span>
              </div>
            )}
            <div
              ref={tableWrapRef}
              style={{ overflowX: "auto", position: "relative" }}
            >
              {showScrollHint && (
                <div style={{
                  position: "absolute",
                  top: 0,
                  right: 0,
                  width: "40px",
                  height: "100%",
                  background: "linear-gradient(to right, transparent, rgba(255,255,255,0.85))",
                  pointerEvents: "none",
                  zIndex: 1,
                }} />
              )}
              <table style={S.table}>
                <thead>
                  <tr>
                    {["目撃日時", "車号", "営業所", "路線", "始発地", "コメント", ""].map((h) => (
                      <th key={h} style={S.th}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sightings.map((s) => (
                    <tr key={s.id}>
                      <td style={{ ...S.td, whiteSpace: "nowrap" }}>{s.started_at}</td>
                      <td style={{ ...S.td, fontWeight: "600" }}>{s.bus_number}</td>
                      <td style={S.td}>{s.depot}</td>
                      <td style={S.td}>{s.route}</td>
                      <td style={S.td}>{s.start}</td>
                      <td style={{ ...S.td, color: "#6b7280", maxWidth: "160px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {s.comment || "—"}
                      </td>
                      <td style={{ ...S.td, textAlign: "center" }}>
                        <button
                          onClick={() => handleDelete(s)}
                          disabled={deletingId === s.id}
                          style={{
                            background: "none",
                            border: "1px solid #fca5a5",
                            color: "#dc2626",
                            borderRadius: "6px",
                            padding: "3px 8px",
                            fontSize: "0.75rem",
                            fontWeight: "600",
                            cursor: deletingId === s.id ? "not-allowed" : "pointer",
                            opacity: deletingId === s.id ? 0.5 : 1,
                            whiteSpace: "nowrap",
                          }}
                        >
                          {deletingId === s.id ? "削除中…" : "削除"}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {lastPage > 1 && (
              <div style={{ marginTop: "12px", textAlign: "center" }}>
                {Array.from({ length: lastPage }, (_, i) => i + 1).map((p) => (
                  <button
                    key={p}
                    style={S.pageBtn(p === page)}
                    onClick={() => setPage(p)}
                  >
                    {p}
                  </button>
                ))}
              </div>
            )}
          </>
        )}
      </div>
      {/* お気に入り追加確認モーダル */}
      {favConfirm && (
        <div style={{
          position: "fixed", inset: 0, zIndex: 2000,
          background: "rgba(0,0,0,0.45)", backdropFilter: "blur(4px)",
          display: "flex", alignItems: "center", justifyContent: "center",
          padding: "16px",
        }}>
          <div style={{
            background: "#fff", borderRadius: "16px", padding: "28px 24px",
            maxWidth: "320px", width: "100%",
            boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
            textAlign: "center",
          }}>
            <div style={{ fontSize: "2rem", marginBottom: "12px" }}>⭐</div>
            <p style={{ color: "#065f46", fontWeight: "700", fontSize: "1rem", marginBottom: "6px" }}>
              お気に入りに追加しますか？
            </p>
            <p style={{ color: "#1f2937", fontSize: "1.3rem", fontWeight: "800", marginBottom: "4px", letterSpacing: "0.05em" }}>
              {favConfirm?.depot} {favConfirm?.bus_number}号車
            </p>
            {favConfirm?.retired_at && (
              <p style={{ color: "#dc2626", fontSize: "0.78rem", marginBottom: "12px" }}>※ 廃車済み車両です</p>
            )}
            <div style={{ display: "flex", gap: "10px" }}>
              <button
                style={{ ...S.btnSecondary, flex: 1 }}
                onClick={() => setFavConfirm(null)}
                disabled={favAdding}
              >
                キャンセル
              </button>
              <button
                style={{ ...S.btnPrimary, flex: 1 }}
                onClick={handleFavConfirm}
                disabled={favAdding}
              >
                {favAdding ? "追加中…" : "追加する"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
